import { inject, Injectable, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { HttpService, QueryParam } from './http.service';
import { Observable, tap } from 'rxjs';
import { PaginationModel } from '@models/shared/pagination.model';
import {
  CloseSessionRequest,
  OpenSessionRequest,
  SessionListFilters,
  SessionModel,
} from '@models/session/session.model';
import { TerminalService } from './terminal.service';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private readonly sessionsApi = `${environment.caisseApiUrl}/self-pos-terminal-sessions`;
  private readonly httpService = inject(HttpService);
  private readonly terminalService = inject(TerminalService);
  get terminalId(): number | undefined {
    return this.terminalService.currentTerminal()?.id;
  }

  readonly lastSession = signal<SessionModel | null>(null);

  getLastSession(): Observable<SessionModel> {
    return this.httpService
      .get<SessionModel>(`${this.sessionsApi}/last/self-pos-terminal/${this.terminalId}`)
      .pipe(tap((session) => this.lastSession.set(session)));
  }

  getSessions(
    page: number,
    limit: number,
    filters: SessionListFilters,
  ): Observable<PaginationModel<SessionModel>> {
    const params: Record<string, QueryParam> = {
      page,
      limit,
      sort: `${filters.sort}:${filters.order}`,
    };
    if (filters.status !== 'all') {
      params['status'] = filters.status;
    }
    if (filters.sessionNumber !== null) {
      params['id'] = filters.sessionNumber;
    }
    if (filters.date) {
      params['date'] = filters.date;
    }

    return this.httpService.get<PaginationModel<SessionModel>>(
      `${this.sessionsApi}/self-pos-terminal/${this.terminalId}`,
      params,
    );
  }

  openSession(request: OpenSessionRequest): Observable<SessionModel> {
    return this.httpService.post<SessionModel>(this.sessionsApi, request);
  }

  closeSession(request: CloseSessionRequest): Observable<SessionModel> {
    return this.httpService
      .put<SessionModel>(`${this.sessionsApi}`, request)
      .pipe(tap((session) => this.lastSession.set(session)));
  }
}
